import { Home } from "lucide-react";
import { Link } from "react-router-dom";
import HeaderToolbar from "@/components/layout/HeaderToolbar";
import {
  headerButtonClassName,
  headerToolClassName,
} from "@/components/layout/headerButtonClassName";
import { Button } from "@/components/ui/button";
import { useHeaderRoute } from "@/hooks/layout/useHeaderRoute";

const renderCourseTitle = (title: string | null) => {
  if (!title) {
    return (
      <div className="h-7 w-48 rounded bg-primary-foreground/20 animate-pulse" />
    );
  }

  return (
    <span
      className="block min-w-0 truncate text-lg font-semibold leading-tight"
      title={title}
    >
      {title}
    </span>
  );
};

const NavLink = ({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) => (
  <Button
    variant="outline"
    size="sm"
    className={[
      "border-transparent text-primary-foreground",
      headerButtonClassName,
      headerToolClassName(active),
    ].join(" ")}
    asChild
  >
    <Link to={to}>{children}</Link>
  </Button>
);

const renderDashboardLink = (href: string, active: boolean) => (
  <nav className="flex shrink-0 items-center gap-2">
    <NavLink to={href} active={active}>
      <Home className="size-4" />
      Dashboard
    </NavLink>
  </nav>
);

export const Header = () => {
  const {
    courseTitle,
    loading,
    enrollmentId,
    showDashboard,
    isDashboard,
    isTrainerDashboard,
  } = useHeaderRoute();
  // Outside an enrollment the control still opens the home redirect.
  const dashboardHref = enrollmentId
    ? `/course/${enrollmentId}/dashboard`
    : "/";

  return (
    <header className="sticky top-0 z-50 flex justify-between w-full items-center h-16 px-4 md:px-8 border-b bg-primary text-primary-foreground">
      {/* Left section: Logo + Nav + Course Info */}
      <div className="flex min-w-0 flex-1 items-center gap-4 md:gap-6">
        <img src="/logo.svg" alt="ILP" className="w-auto shrink-0 h-10" />

        {showDashboard && renderDashboardLink(dashboardHref, isDashboard)}

        {(courseTitle || loading) && (
          <div className="hidden min-w-0 sm:flex flex-col border-l border-primary-foreground/30 px-4 md:px-6">
            {isTrainerDashboard && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-primary-foreground/70">
                Lernbegleiter Dashboard
              </span>
            )}
            {renderCourseTitle(courseTitle)}
          </div>
        )}
      </div>

      <HeaderToolbar />
    </header>
  );
};
